import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';
import { ObjectResponse } from '@/src/types/common';
import { APIError } from '@/src/models/common/apiError';
import useAuthStore from '@/src/store/useAuthStore';
import { ERROR_CODE } from '@/src/constants/api/resultCode';
import useDialogStore from '@/src/store/useDialogStore';

interface AxiosParams {
  method: Method;
  requestBody?: unknown;
  queryParams?: unknown;
  url: string;
  config?: AxiosRequestConfig;
}

type Method = 'get' | 'post' | 'put' | 'delete';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_ROOT_URL,
  timeout: 60000,
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
  withCredentials: false,
  paramsSerializer: function (params) {
    return qs.stringify(params, { arrayFormat: 'brackets' });
  },
});

const request = <T = ObjectResponse<any>>({
  method,
  requestBody,
  queryParams,
  url,
  config,
}: AxiosParams) => {
  switch (method) {
    case 'get':
      return axiosInstance.get<T>(url, { params: queryParams, ...config });
    case 'post':
      return axiosInstance.post<T>(url, requestBody);
    case 'put':
      return axiosInstance.put<T>(url, requestBody);
    case 'delete':
      return axiosInstance.delete<T>(url);
  }
};

axiosInstance.interceptors.request.use(
  function (config) {
    const token = useAuthStore.getState().token;
    if (token) config.headers.setAuthorization(token);
    if (config.params)
      Object.keys(config.params).forEach(key => {
        const queryValue = config.params[key];
        if (queryValue === null) config.params[key] = undefined;
        if (typeof queryValue === 'string' && queryValue === '') {
          config.params[key] = undefined;
        }
        if (typeof queryValue === 'object' && !Array.isArray(queryValue))
          config.params[key] = JSON.stringify(config.params[key]);
      });

    if (config.data) {
      Object.keys(config.data).forEach(key => {
        const dataValue = config.data[key];

        if (typeof dataValue === 'string' && dataValue) {
          if (config.data[key].trimEnd() !== '')
            config.data[key] = config.data[key].trimEnd();
        }
        if (typeof dataValue === 'string' && !dataValue) {
          config.data[key] = null;
        }
      });
    }

    return config;
  },
  error => Promise.reject(new APIError(error)),
);
axiosInstance.interceptors.response.use(
  response => response, // 2xx 범위에 있는 상태 코드
  error => {
    if (error?.code === 'ECONNABORTED') {
      const { setOpen, setTitleText, setBodyText } = useDialogStore.getState();
      setTitleText('오류');
      setBodyText(
        '일시적인 장애로 서비스를 이용할 수 없습니다\n잠시 후에 다시 시도해주세요',
      );
      setOpen();
    }
    if (
      [
        ERROR_CODE.COMMON_ERROR_MESSAGE_BEARER_TOKEN_NEEDED,
        ERROR_CODE.JWT_INVALID_TOKEN,
        ERROR_CODE.ACCOUNT_HISTORY_NOT_FOUND_TOKEN,
        ERROR_CODE.ACCOUNT_INACTIVATED_ACCOUNT,
      ].includes(error?.response?.data?.errorCode)
    ) {
      const { signOut } = useAuthStore.getState();
      const { setOpen, setTitleText, setBodyText, setHasCloseButton } =
        useDialogStore.getState();
      signOut();

      setTitleText('세션이 만료되어 로그아웃 되었습니다. 다시 로그인해주세요.');
      setHasCloseButton(false);

      setOpen();
    }
    return Promise.reject(new APIError(error));
  }, // 2xx 외의 범위에 있는 상태 코드
);

export default request;
